/**
 * INTRO RUNNER (B6): one run of the intro, outside React (spec §E.7).
 *
 *   const run = new IntroRunner({ choreo, mode, onView, onDone });
 *   run.start();          // phase → "intro" (from loading/explore), then choreo.intro({ mode, signal })
 *   run.skip();           // from 600 ms until the ui beat: abort → the map jumps to its final state
 *   run.cancel({ settle }) // unmount: stop listening; mid-intro, abort the map's intro too
 *
 * Phase and events it owns:
 *   start              phase "loading" | "explore" → "intro"
 *   "ui" beat          phase "intro" → "explore" (B7's panel, B9's hud and B10's chrome arrive on it)
 *   intro resolves     markIntroSeen() · bus "intro:done" {skipped} · onDone(skipped)
 *                      (B4 emits only intro:beat; intro:done is ours)
 *
 * The choreographer resolves intro() in the same tick as its "done" beat, so the finish is keyed off
 * the promise, not the beat: a late "done" from an aborted earlier run (it fires 420 ms after the
 * abort) can never end this one. A watchdog finishes the run anyway if the map is destroyed mid-intro
 * (its promise then never settles).
 *
 * Reduced motion always runs the short intro (§E.10), whatever mode is asked for.
 */
import type { BusEvents, IntroBeat } from "@/contracts/bus";
import type { Choreographer } from "@/contracts/map";
import type { AppActions, AppState } from "@/contracts/store";
import { bus, on as busOn } from "@/store/bus";
import { actions as storeActions, getApp } from "@/store/store";
import { INTRO_TIMING, canSkip, expectedEndMs, initialView, reduceIntro, type IntroEvent, type IntroMode, type IntroView } from "./introModel";

export interface IntroRunnerOptions {
  choreo: Choreographer;
  mode: IntroMode;
  onView?: (v: IntroView) => void;
  onDone?: (skipped: boolean) => void;
  /* injectables (tests) */
  on?: <K extends keyof BusEvents>(type: K, fn: (e: BusEvents[K]) => void) => () => void;
  emit?: <K extends keyof BusEvents>(type: K, e: BusEvents[K]) => void;
  getState?: () => Pick<AppState, "phase" | "reducedMotion">;
  actions?: Pick<AppActions, "setPhase" | "markIntroSeen">;
}

type Timer = ReturnType<typeof setTimeout>;

/** The run on screen (one at a time). */
const registry: { active: IntroRunner | null } = { active: null };

/** Skips the running intro, if it can be skipped now (window.__ld.skipIntro, QA). */
export function skipIntro(): boolean {
  return registry.active?.skip() ?? false;
}

/** The run on screen, if any (harness and QA). */
export function activeIntro(): IntroRunner | null {
  return registry.active;
}

export class IntroRunner {
  readonly mode: IntroMode;
  private v: IntroView;
  private readonly ac = new AbortController();
  private readonly timers = new Set<Timer>();
  private offBeat: (() => void) | null = null;
  private startCalled = false;
  private startSeen = false;
  private finished = false;
  private disposed = false;

  constructor(private readonly o: IntroRunnerOptions) {
    const reduced = this.state().reducedMotion;
    this.mode = o.mode === "full" && !reduced ? "full" : "short";
    this.v = initialView(this.mode);
  }

  get view(): IntroView {
    return this.v;
  }
  get isFinished(): boolean {
    return this.finished;
  }

  start(): void {
    if (this.startCalled || this.disposed) return;
    this.startCalled = true;
    registry.active = this;
    const on = this.o.on ?? busOn;
    // Subscribe before calling intro(): "start" (and, in the short intro, "ui") fire synchronously inside it.
    this.offBeat = on("intro:beat", ({ beat }) => this.onBeat(beat));
    const p = this.state().phase;
    if (p === "loading" || p === "explore") this.act().setPhase("intro");

    let run: Promise<void>;
    try {
      run = this.o.choreo.intro({ mode: this.mode, signal: this.ac.signal });
    } catch (err) {
      console.error("[intro]", err);
      run = Promise.resolve();
    }
    this.later(expectedEndMs(this.mode), () => this.finish());
    void run.then(
      () => this.finish(),
      () => this.finish(),
    );
  }

  /** User skip. true when it took effect. */
  skip(): boolean {
    if (this.finished || this.disposed || !canSkip(this.v)) return false;
    this.apply("skip");
    this.ac.abort(); // the choreographer jumps to the final state: "ui" now, "done" + resolve after 420
    return true;
  }

  /**
   * Stop listening. settle: when the run had not finished, the map's intro is aborted (it settles in its
   * final state) and the app is released as if the intro had been skipped (phase explore, intro:done),
   * without onDone. settle false (the map was replaced): abort quietly.
   */
  cancel(o: { settle: boolean }): void {
    if (this.disposed) return;
    const running = this.startCalled && !this.finished;
    this.dispose();
    if (!running) return;
    this.finished = true;
    this.ac.abort();
    if (!o.settle) return;
    if (this.state().phase === "intro") this.act().setPhase("explore");
    this.act().markIntroSeen();
    this.emit("intro:done", { skipped: true });
  }

  /* ── internals ─────────────────────────────────────────────────── */

  private onBeat(beat: IntroBeat): void {
    if (this.finished || this.disposed) return;
    if (beat === "start") {
      if (this.startSeen) return;
      this.startSeen = true;
      this.apply("start");
      if (this.mode === "full") this.later(INTRO_TIMING.skipAfterMs, () => this.apply("skip-in"));
      return;
    }
    // Beats before our own "start" belong to another run; "done" is handled by the promise.
    if (!this.startSeen || beat === "done") return;
    this.apply(beat);
    if (beat === "line1") this.later(INTRO_TIMING.line1HoldMs, () => this.apply("line1-out"));
    else if (beat === "line2") this.later(INTRO_TIMING.line2HoldMs, () => this.apply("line2-out"));
    else if (beat === "ui" && this.state().phase === "intro") this.act().setPhase("explore");
  }

  private finish(): void {
    if (this.finished || this.disposed) return;
    this.finished = true;
    this.apply("done");
    this.dispose();
    if (this.state().phase === "intro") this.act().setPhase("explore");
    this.act().markIntroSeen();
    const skipped = this.v.skipped;
    this.emit("intro:done", { skipped });
    this.o.onDone?.(skipped);
  }

  private dispose(): void {
    this.disposed = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.offBeat?.();
    this.offBeat = null;
    if (registry.active === this) registry.active = null;
  }

  private apply(e: IntroEvent): void {
    const next = reduceIntro(this.v, e);
    if (next === this.v) return;
    this.v = next;
    this.o.onView?.(next);
  }

  private later(ms: number, fn: () => void): void {
    const t = setTimeout(() => {
      this.timers.delete(t);
      if (!this.disposed) fn();
    }, ms);
    this.timers.add(t);
  }

  private state(): Pick<AppState, "phase" | "reducedMotion"> {
    return (this.o.getState ?? getApp)();
  }
  private act(): Pick<AppActions, "setPhase" | "markIntroSeen"> {
    return this.o.actions ?? storeActions;
  }
  private emit<K extends keyof BusEvents>(type: K, e: BusEvents[K]): void {
    if (this.o.emit) this.o.emit(type, e);
    else (bus.emit as <T extends keyof BusEvents>(t: T, ev: BusEvents[T]) => void)(type, e);
  }
}
